<template>
  <div
    class="squeezely-product-slider"
  >
    <div
      :id="id"
      class="squeezely-product-slider__input"
    />

    <section
      v-if="data"
      :id="data.id"
      class="sqzly-personalization squeezely-product-slider__container"
    >
      <ProductSlider
        :products="data.products"
        class="squeezely-product-slider__slider"
        :product-linkable="false"
      >
        <div :class="headingClass || `heading heading--3`">
          {{ data.heading }}
        </div>
      </ProductSlider>
    </section>
  </div>
</template>

<script lang="ts" setup>
type squeezelyData = {
  id: string
  className: string
  personalizationId?: string
  variantId?: string
  variantRevision?: string
  rawData: any
  heading: string
  products: any[]
}

const props = defineProps<{
  id: string
  headingClass?: string | string[] | Record<string, any>
}>()

const data = ref<squeezelyData | null>(null)

let targetNode: HTMLElement | null = null
let observer: MutationObserver | null = null

// Check if content changed and retrigger parsing
const config = { attributes: false, childList: true, subtree: false }

// Callback function to execute when mutations are observed
const callback = (mutationsList) => {
  mutationsList.forEach((mutation) => {
    if (mutation.type === 'childList') {
      if (mutation.addedNodes?.length) {
        console.log('A child node has been added.', mutation)
        processInjectData(targetNode)
      }

      if (mutation.removedNodes?.length) {
        console.log('A child node has been removed.', mutation)
      }
    }
  })
}

const processInjectData = (targetNode: HTMLElement) => {
  const squeezelyElement = targetNode.querySelector('[id^="sqzl"]')
  const squeezelyChildElement = targetNode.querySelector('.sqzly-personalization.sqzly-custom')
  // console.log('containerElement', targetNode, squeezelyElement, squeezelyChildElement)

  if (!squeezelyElement || !squeezelyChildElement) {
    return
  }

  squeezelyChildElement.querySelectorAll('*').forEach((node) => {
    node.remove()
  })

  const rawData = squeezelyChildElement.textContent
  // console.log('squeezely rawData', rawData)

  const parsedData = JSON.parse(rawData || '')
  // console.log('squeezely data', data)
  // console.log('squeezely id', squeezelyElement.id)
  // console.log('squeezely classList', squeezelyElement.classList)

  const attributesObject = {
    id: squeezelyElement.id,
    className: squeezelyElement.className,
    ...(/sqzl_div-(?<variantId>[\d]+)-(?<variantRevision>[\d]+)/.exec(
      squeezelyElement.id,
    )?.groups || {}),
    ...(/sqzly-(?<personalizationId>[\d]+)/.exec(
      squeezelyElement.className,
    )?.groups || {}),
  }

  data.value = {
    ...attributesObject,
    ...parsedData,
    products:
      parsedData?.products
        ?.filter(
          (squeezelyProduct: any) => !!squeezelyProduct,
        )
        ?.map((squeezelyProduct: any) => {
          const product: any = {
            id: +(squeezelyProduct?.productUrl?.split('-')?.pop() || 0),
            ...squeezelyProduct,
            squeezely: attributesObject,
          }

          delete product.productUrl

          return product
        }) || [],
  }
}

onMounted(() => {
  targetNode = document.getElementById(props.id)

  if (!targetNode) {
    return
  }

  // Create an observer instance linked to the callback function
  observer = new MutationObserver(callback)

  // Start observing the target node for configured mutations
  observer.observe(targetNode, config)

  // Check if data is already set
  processInjectData(targetNode)
})

onUnmounted(() => {
  // Cleanup MutationObserver
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<style>
@import url("~layers/app/components/Thirdparty/SqueezelyProductSlider.css");
</style>
